import { ComponentProps, Suspense, lazy } from 'react';

import { cn } from '../helpers/cn';

const ReactPlayer = lazy(() => import('react-player'));

const VIMEO_VIDEO_URL = /^https?:\/\/(?:www\.|player\.)?vimeo\.com\/(?:video\/|event\/)?\d+/;

// Session recordings are Vimeo share links (e.g. https://vimeo.com/386391398/8fb38b0466), but the
// field can also hold sentinel values such as `VIDEO_ARCHIVED`, which are not playable.
export const isVimeoVideoUrl = (url?: string | null): boolean => !!url && VIMEO_VIDEO_URL.test(url);

export type VimeoConfig = NonNullable<NonNullable<ComponentProps<typeof ReactPlayer>['config']>['vimeo']>;

/**
 * Vimeo embed params (they become query params on the player iframe) that leave only the play
 * button, progress bar, volume, settings, picture-in-picture and fullscreen controls.
 */
export const DEFAULT_VIMEO_CONFIG: VimeoConfig = {
  play_button_position: 'bottom',
  progress_bar: true,
  volume: true,
  quality_selector: true,
  speed: false,
  pip: true,
  fullscreen: true,
  airplay: false,
  chromecast: false,
  cc: false,
  chapters: false,
  transcript: false,
  audio_tracks: false,
  watch_full_video: false,
  vimeo_logo: false,
  title: false,
  byline: false,
  portrait: false,
};

export interface VimeoPlayerProps {
  /** A vimeo.com share link or a player.vimeo.com embed link. */
  videoUrl: string;
  className?: string;
  autoPlay?: boolean;
  /** Vimeo embed params merged over `DEFAULT_VIMEO_CONFIG`. */
  config?: VimeoConfig;
}

const VimeoPlayer = ({ videoUrl, className, autoPlay = false, config }: VimeoPlayerProps) => {
  if (!isVimeoVideoUrl(videoUrl)) {
    return null;
  }

  return (
    <div className={cn('aspect-video w-full overflow-hidden rounded-lg bg-black', className)}>
      <Suspense fallback={null}>
        <ReactPlayer
          src={videoUrl}
          controls
          playing={autoPlay}
          width="100%"
          height="100%"
          config={{ vimeo: { ...DEFAULT_VIMEO_CONFIG, ...config } }}
        />
      </Suspense>
    </div>
  );
};

export default VimeoPlayer;
